import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Field,
  Fieldset,
  Form,
  Modal,
  ModalProps,
  RawInput,
  Textarea,
} from '@payaca/ambrosia';
import { FC, type JSX } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod/v4';

import useCreateStockLocation from '@/api/mutations/stockLocations/useCreateStockLocation';
import useUpdateStockLocation from '@/api/mutations/stockLocations/useUpdateStockLocation';
import { StockLocationsQuery } from '@/gql/graphql';

import { AddressLookupOrInput } from '../addressLookupOrInput/AddressLookupOrInput';

export type StockLocationListItem =
  StockLocationsQuery['stockLocations']['items'][number];

const stockLocationFormSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .max(255, 'Name must be 255 characters or less'),
  address: z.object({
    line1: z.string().min(1, 'Address line 1 is required'),
    line2: z.string().optional(),
    city: z.string().optional(),
    administrativeArea: z.string().optional(),
    postcode: z.string().optional(),
    country: z.string().optional(),
  }),
  notes: z.string().optional(),
});

type TFormState = z.infer<typeof stockLocationFormSchema>;

const toDefaultValues = (
  stockLocation?: StockLocationListItem
): TFormState => ({
  name: stockLocation?.name ?? '',
  address: {
    line1: stockLocation?.address.line1 ?? '',
    line2: stockLocation?.address.line2 ?? '',
    city: stockLocation?.address.city ?? '',
    administrativeArea: stockLocation?.address.administrativeArea ?? '',
    postcode: stockLocation?.address.postalCode ?? '',
    country: stockLocation?.address.country ?? '',
  },
  notes: stockLocation?.notes ?? '',
});

type Props = {
  stockLocation?: StockLocationListItem;
} & Pick<ModalProps, 'isOpen' | 'onClose'>;

const CreateEditStockLocationBody: FC<Omit<Props, 'isOpen'>> = ({
  onClose,
  stockLocation,
}): JSX.Element => {
  const isEditing = !!stockLocation;
  const { mutate: createStockLocation, isPending: isCreating } =
    useCreateStockLocation({ onSuccess: () => onClose?.() });
  const { mutate: updateStockLocation, isPending: isUpdating } =
    useUpdateStockLocation({ onSuccess: () => onClose?.() });

  const formMethods = useForm<TFormState>({
    resolver: zodResolver(stockLocationFormSchema),
    defaultValues: toDefaultValues(stockLocation),
  });
  const { dirtyFields } = formMethods.formState;

  const onSubmit = (state: TFormState) => {
    const address = {
      new: {
        line1: state.address.line1,
        line2: state.address.line2 || undefined,
        city: state.address.city || undefined,
        administrativeArea: state.address.administrativeArea || undefined,
        postcode: state.address.postcode || undefined,
        country: state.address.country || undefined,
      },
    };

    if (isEditing) {
      // The address always replaces the Location's address, so only send it
      // when the user actually changed it — a rename leaves it untouched.
      updateStockLocation({
        id: stockLocation.id,
        name: state.name,
        notes: state.notes || null,
        address: dirtyFields.address ? address : undefined,
      });
    } else {
      createStockLocation({
        name: state.name,
        notes: state.notes || undefined,
        address,
      });
    }
  };

  return (
    <Form methods={formMethods} onSubmit={onSubmit}>
      <Modal.Body>
        <Fieldset>
          <Field name="name">
            <Field.Label>Name</Field.Label>
            <RawInput {...formMethods.register('name')} />
          </Field>

          <Field name="address.line1">
            <Field.Label>Address</Field.Label>
            <Controller
              name="address"
              render={({ field: { value, onChange } }) => (
                <AddressLookupOrInput
                  address={value}
                  onChange={(address) => onChange(address ?? {})}
                />
              )}
            />
          </Field>

          <Field name="notes">
            <Field.Label>Notes</Field.Label>
            <Controller
              name="notes"
              render={({ field: { onChange, value } }) => (
                <Textarea
                  value={value}
                  onChange={onChange}
                  rows={2}
                  autoHeight
                />
              )}
            />
          </Field>
        </Fieldset>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Actions>
          <Button onClick={onClose} variant="secondary" type="button">
            Cancel
          </Button>
          <Button type="submit" isProcessing={isCreating || isUpdating}>
            Save
          </Button>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </Form>
  );
};

const CreateEditStockLocationModal: FC<Props> = ({
  isOpen,
  onClose,
  stockLocation,
}): JSX.Element => {
  return (
    <Modal
      isOpen={isOpen}
      title={stockLocation ? 'Edit stock location' : 'Create stock location'}
      onClose={onClose}
      size="md"
      disableBackdropClick
    >
      {/* Remount the form on open / target change so its defaults reset. */}
      {isOpen && (
        <CreateEditStockLocationBody
          key={stockLocation?.id ?? 'create'}
          onClose={onClose}
          stockLocation={stockLocation}
        />
      )}
    </Modal>
  );
};

export default CreateEditStockLocationModal;
